import { useCallback } from 'react'
import { useNavigate, type NavigateFunction } from 'react-router'
import posthog from 'posthog-js'
import { achatNumeroEnAttente, choixNumeroARefaire, fetchCompanies, fetchProfile, type CompanyInfo, type UserProfile } from '@/api/auth'
import { useAuthStore } from '@/stores/useAuthStore'
import { tokenNeedsVerification } from '@/lib/verificationConnexion'
import { defaultLocale, isLocale } from '@/lib/locales'

interface RedirectContext {
  navigate: NavigateFunction
  locale: string
}

function identifyPostHog(profile: UserProfile) {
  try {
    const userEmail = profile.email || ''
    posthog.identify(profile.id?.toString() || userEmail, {
      email: userEmail,
      name: `${profile.first_name || ''} ${profile.last_name || ''}`.trim(),
      role: profile.role || 'MANAGER',
      company_id: profile.company?.toString() || '',
      company_name: profile.company_name || '',
    })
    if (profile.company) {
      posthog.group('company', profile.company.toString(), {
        name: profile.company_name || '',
      })
    }
  } catch {
    // PostHog non initialisé en local — non bloquant
  }
}

function buildRedirectFromQuery(preferredLocale: string): string | null {
  const params = new URLSearchParams(window.location.search)
  const redirectParam = params.get('redirect') || params.get('next')
  if (!redirectParam) return null

  if (redirectParam.includes('/call?number=')) {
    const url = new URL(redirectParam, window.location.origin)
    const number = url.searchParams.get('number')
    return number
      ? `/${preferredLocale}/dashboard?call=${encodeURIComponent(number)}`
      : `/${preferredLocale}/dashboard`
  }

  if (redirectParam.startsWith(`/${preferredLocale}/`)) return redirectParam

  let cleanPath = redirectParam
  if (cleanPath.startsWith('/en/') || cleanPath.startsWith('/fr/')) {
    cleanPath = cleanPath.substring(3)
  }
  return `/${preferredLocale}${cleanPath.startsWith('/') ? cleanPath : '/' + cleanPath}`
}

async function routeManager(
  ctx: RedirectContext,
  profile: UserProfile,
  preferredLocale: string,
): Promise<void> {
  // Une erreur (429, réseau, 500) ne dit rien de l'état du compte : on
  // réessaie, puis on laisse OnboardingGuard trancher sur /manager. Jamais
  // l'onboarding par défaut, un client payant s'y est retrouvé.
  let companies: { companies?: CompanyInfo[] } | null = null
  for (const delai of [0, 1000, 3000]) {
    if (delai) await new Promise((r) => setTimeout(r, delai))
    try {
      companies = await fetchCompanies()
      break
    } catch {
      // tentative suivante
    }
  }
  if (!companies) {
    ctx.navigate(`/${preferredLocale}/manager`, { replace: true })
    return
  }

  const company = companies.companies?.[0]

  // Cas 1 : client actif (numéro Twilio acquis)
  if (company?.has_active_number || company?.phone_number) {
    const target = buildRedirectFromQuery(preferredLocale) || `/${preferredLocale}/manager`
    ctx.navigate(target, { replace: true })
    return
  }

  // Payé mais numéro pas encore acheté : l'écran Bienvenue l'achète.
  if (achatNumeroEnAttente(company)) {
    ctx.navigate(`/${preferredLocale}/onboarding/welcome`, { replace: true })
    return
  }

  // Paid, but the chosen number could not be bought: choose another one.
  if (choixNumeroARefaire(company)) {
    ctx.navigate(`/${preferredLocale}/onboarding/step1?indisponible=1`, { replace: true })
    return
  }

  // Cas 2 : user qui a un abonnement encore valide MAIS plus de numéro
  // (a supprimé son numéro Twilio, ou annulé son abo avec grâce de fin de
  // période). On ne le renvoie PAS dans l'onboarding — il a déjà payé. On
  // l'amène au manager où il peut racheter un numéro depuis l'onglet Numéros
  // ou voir l'état de son abonnement.
  if (company?.has_active_subscription) {
    ctx.navigate(`/${preferredLocale}/manager?view=numbers`, { replace: true })
    return
  }

  // Cas 3 : onboarding en cours (a sélectionné un numéro préféré mais pas
  // encore acheté)
  if (company?.preferred_phone_number) {
    const hasStep2 = company.name && !company.name.startsWith('Nouvelle entreprise')

    if (!hasStep2) {
      ctx.navigate(`/${ctx.locale}/onboarding/step2`, { replace: true })
      return
    }
    // Onboarding complet : on reste dans Vite sur la page subscribe (sélection plan)
    ctx.navigate(`/${ctx.locale}/subscribe`, { replace: true })
    return
  }

  // Cas 4 : vraiment un nouveau client (jamais payé, jamais sélectionné de numéro)
  ctx.navigate(`/${ctx.locale}/onboarding/step1`, { replace: true })

  // Marquer profile comme utilisé
  void profile
}

/**
 * Hook de routage post-login. Récupère le profil + companies et navigue vers
 * la bonne route SPA (ou redirige vers le marketing si onboarding terminé).
 */
export function usePostLoginRedirect() {
  const navigate = useNavigate()
  const setUser = useAuthStore((s) => s.setUser)

  return useCallback(
    async (locale: string) => {
      // Risky sign-in: the whole API is closed until the code is confirmed.
      // The query (?next=…) is kept so the code screen can resume the redirect.
      if (tokenNeedsVerification(useAuthStore.getState().accessToken)) {
        navigate(`/${locale}/verification${window.location.search}`, { replace: true })
        return
      }

      const profile = await fetchProfile()

      // The language the person signed in with wins over the one saved on the
      // account: someone who logs in in English must not land in French.
      const preferredLocale = isLocale(locale) ? locale : profile.language || defaultLocale
      localStorage.setItem('preferred_locale', preferredLocale)

      setUser({
        id: profile.id,
        email: profile.email,
        firstName: profile.first_name,
        lastName: profile.last_name,
        role: profile.role,
        language: profile.language,
        companyId: profile.company,
        companyName: profile.company_name,
      })

      identifyPostHog(profile)

      const ctx: RedirectContext = { navigate, locale }
      const userRole = profile.role || 'MANAGER'

      // AGENT : direct au dashboard (avec ?redirect/?next éventuel)
      if (userRole === 'AGENT') {
        const target = buildRedirectFromQuery(preferredLocale) || `/${preferredLocale}/dashboard`
        navigate(target, { replace: true })
        return
      }

      // MANAGER : check companies
      await routeManager(ctx, profile, preferredLocale)
    },
    [navigate, setUser],
  )
}
